import type { CSSProperties } from 'react';
import { devWarn } from './internal/devWarn';

/**
 * Escape-hatch props per ADR-017 §3.
 *
 * - `unsafe_*`         — versioned; semver-major to remove
 * - `__experimental_*` — can break in minor
 * - `__internal_*`     — can break in patch; Jacquard-only
 */

/** Visible escape hatch — className surface only.
 *  Use when a primitive wants to expose `unsafe_className` for class-list
 *  injection without also opting into `unsafe_style`. */
export interface UnsafeClassNameProps {
  /** Escape-hatch className. Merged after Jacquard classes. */
  unsafe_className?: string;
}

/** Visible escape hatch — inline-style surface only.
 *  Use when a primitive wants `unsafe_style` (CSSProperties forwarded
 *  to the rendered element) but NOT `unsafe_className`. Most primitives
 *  that previously consumed `StyledProps.style` extend this interface
 *  (audit #11, 2026-05-16). */
export interface UnsafeStyleProps {
  /** Escape-hatch inline style. Use Tier 3 CSS vars instead when possible. */
  unsafe_style?: CSSProperties;
}

/** Both escape hatches. Reserved for primitives that opt into BOTH
 *  surfaces (Card, Field, CreatableSelect, Select) — historically the
 *  default before the split. New primitives should usually pick the
 *  narrower variant. */
export interface UnsafeProps extends UnsafeClassNameProps, UnsafeStyleProps {}

/** Runtime warning for __internal_ props used outside Jacquard. */
export function warnIfExternal(propName: string): void {
  // Simple heuristic: warn unconditionally in dev; the ESLint rule
  // catches it at lint time in adopter code.
  devWarn(
    `[Jacquard] ${propName} is an internal prop (__internal_*). ` +
      `It may change or be removed in any patch release. ` +
      `Do not use in adopter code (ADR-017 §3).`,
  );
}
